<template>
  <transition name="modal">
    <div v-if="show" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-5xl w-full transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">
            Generate Classifier with AI
          </h3>
        </div>

        <!-- Generation Step -->
        <div v-if="!aiGenerated" class="p-6 space-y-4 overflow-y-auto">
          <!-- Configuration Options Section -->
          <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 class="text-sm font-semibold text-gray-900 mb-3">Configuration Options</h4>
            <p class="text-xs text-gray-600 mb-4">
              Choose what data sources the classifier should use. The AI will generate a prompt that matches your selections.
            </p>

            <div class="space-y-4">
              <!-- Web Search Option -->
              <label class="flex items-start space-x-3">
                <input
                  v-model="configOptions.enableWebSearch"
                  type="checkbox"
                  class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                />
                <div>
                  <div class="text-sm font-medium text-gray-900">Enable Web Search</div>
                  <div class="text-xs text-gray-500">
                    Allow the classifier to search the web for publicly available information about entities.
                    Useful when internal data is limited.
                  </div>
                </div>
              </label>

              <!-- Performance Data Sources -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Performance Data Sources
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <label
                    v-for="source in availableDataSources"
                    :key="source.key"
                    class="flex items-start space-x-2 p-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      v-model="configOptions.performanceDataSources"
                      type="checkbox"
                      :value="source.key"
                      class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                    />
                    <div class="flex-1">
                      <div class="text-sm font-medium text-gray-900">{{ source.label }}</div>
                      <div class="text-xs text-gray-500">{{ source.description }}</div>
                    </div>
                  </label>
                </div>
                <p class="text-xs text-gray-500 mt-1">
                  Selected: {{ configOptions.performanceDataSources.length }} source(s)
                </p>
              </div>

              <!-- Context Classifiers -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Include Context from Other Classifiers (Optional)
                </label>
                <div class="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                  <label
                    v-for="classifier in availableContextClassifiers"
                    :key="classifier.id"
                    class="flex items-center space-x-2 p-1 hover:bg-gray-50 cursor-pointer rounded"
                  >
                    <input
                      v-model="configOptions.contextClassifiers"
                      type="checkbox"
                      :value="classifier.id"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                    />
                    <div class="flex-1 text-sm">
                      <span class="text-gray-900">{{ classifier.name }}</span>
                      <span class="text-gray-500 text-xs ml-1">({{ classifier.target_entity_type }})</span>
                    </div>
                  </label>
                  <div v-if="availableContextClassifiers.length === 0" class="text-xs text-gray-500 text-center py-2">
                    No other classifiers available
                  </div>
                </div>
                <p class="text-xs text-gray-500 mt-1">
                  Selected: {{ configOptions.contextClassifiers.length }} classifier(s)
                </p>
              </div>
            </div>
          </div>

          <!-- Description Section -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Describe what you want to classify
            </label>
            <textarea
              v-model="aiDescription"
              rows="6"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Example: Classify practices by their VBC readiness based on their AWV rates, cost performance, and participation in value-based contracts. Categories should range from 'Not Ready' to 'VBC Leader'."
            ></textarea>
            <p class="text-xs text-gray-500 mt-1">
              Describe the classification criteria and categories you expect. The AI will generate a prompt based on your configuration options above.
            </p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Classifier Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="aiClassifierName"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., VBC Readiness"
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Target Entity Type <span class="text-red-500">*</span>
              </label>
              <select
                v-model="aiTargetEntityType"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select entity type...</option>
                <option v-for="entityType in entityTypes" :key="entityType.id" :value="entityType.type_name">
                  {{ entityType.type_name }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="aiError" class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-sm text-red-600">{{ aiError }}</p>
          </div>

          <div class="flex justify-end space-x-3">
            <button
              @click="closeGenerator"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="generateWithAI"
              :disabled="!canGenerate || aiGenerating"
              class="btn-primary"
            >
              <FontAwesomeIcon v-if="aiGenerating" :icon="['fas', 'spinner']" class="animate-spin" />
              {{ aiGenerating ? 'Generating...' : 'Generate Configuration' }}
            </button>
          </div>
        </div>

        <!-- Review Step -->
        <div v-else class="flex flex-col flex-1 overflow-hidden">
          <div class="p-6 space-y-4 overflow-y-auto flex-1">
            <!-- Validation Results -->
            <div class="bg-primary-50 rounded-lg p-4 border border-purple-200">
              <div class="flex items-center justify-between mb-3">
                <h4 class="font-semibold text-gray-900">AI Validation Results</h4>
                <div class="flex items-center space-x-2">
                  <span class="text-2xl font-bold" :class="getScoreColor(aiValidation.overall_score)">
                    {{ aiValidation.overall_score }}/100
                  </span>
                  <span class="px-2 py-1 text-xs font-medium rounded-full" :class="getRecommendationBadgeClass(aiValidation.recommendation)">
                    {{ formatRecommendation(aiValidation.recommendation) }}
                  </span>
                </div>
              </div>

              <!-- Validation Categories -->
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div v-for="category in validationCategories" :key="category.key" class="bg-white rounded-lg p-2">
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-600">{{ category.label }}</span>
                    <span class="text-sm font-medium" :class="getScoreColor(aiValidation[category.key]?.score)">
                      {{ aiValidation[category.key]?.score || 0 }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Issues -->
              <div v-if="aiValidation.issues && aiValidation.issues.length > 0" class="mt-3 space-y-2">
                <h5 class="text-sm font-medium text-gray-700">Issues Found:</h5>
                <div v-for="(issue, index) in aiValidation.issues" :key="index"
                     class="flex items-start space-x-2 text-sm bg-white rounded p-2">
                  <FontAwesomeIcon
                    :icon="getIssueIcon(issue.severity)"
                    :class="getIssueColor(issue.severity)"
                  />
                  <div class="flex-1">
                    <p class="text-gray-700">{{ issue.description }}</p>
                    <p v-if="issue.recommendation" class="text-xs text-gray-500 mt-1">
                      Recommendation: {{ issue.recommendation }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Configuration Summary -->
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 class="font-semibold text-gray-900 mb-3">Your Configuration Choices</h4>
              <div class="space-y-2 text-sm">
                <div class="flex items-center">
                  <span class="text-gray-600 w-32">Web Search:</span>
                  <span class="font-medium" :class="aiClassifierConfig.enable_web_search ? 'text-green-600' : 'text-gray-400'">
                    {{ aiClassifierConfig.enable_web_search ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
                <div class="flex items-start">
                  <span class="text-gray-600 w-32">Data Sources:</span>
                  <div class="flex-1">
                    <span v-if="aiClassifierConfig.performance_data_sources?.length > 0" class="font-medium text-gray-900">
                      {{ getDataSourceLabels(aiClassifierConfig.performance_data_sources).join(', ') }}
                    </span>
                    <span v-else class="text-gray-400 italic">None selected</span>
                  </div>
                </div>
                <div class="flex items-start">
                  <span class="text-gray-600 w-32">Context Tags:</span>
                  <div class="flex-1">
                    <span v-if="aiClassifierConfig.include_infotag_types?.length > 0" class="font-medium text-gray-900">
                      {{ aiClassifierConfig.include_infotag_types.length }} classifier(s)
                    </span>
                    <span v-else class="text-gray-400 italic">None selected</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Feedback Section -->
            <div v-if="showFeedbackInput" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 class="font-semibold text-gray-900 mb-2">Provide Feedback for Improvement</h4>
              <textarea
                v-model="aiFeedback"
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                placeholder="Describe what needs to be fixed. E.g., 'Add a category for practices that are just starting', 'The prompt should emphasize cost performance more', etc."
              ></textarea>
              <div class="flex justify-end space-x-2 mt-3">
                <button
                  @click="showFeedbackInput = false; aiFeedback = ''"
                  class="px-3 py-1 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  @click="regenerateWithFeedback"
                  :disabled="!aiFeedback || aiGenerating"
                  class="px-3 py-1 text-sm text-white bg-yellow-600 rounded hover:bg-yellow-700 disabled:opacity-50"
                >
                  <FontAwesomeIcon v-if="aiGenerating" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
                  Regenerate with Feedback
                </button>
              </div>
            </div>

            <!-- Generated Configuration -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Classification Prompt -->
              <div class="md:col-span-2">
                <h4 class="font-semibold text-gray-900 mb-2">Classification Prompt</h4>
                <div class="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ aiClassifierConfig.classification_prompt }}</p>
                </div>
              </div>

              <!-- InfoTags -->
              <div class="md:col-span-2">
                <h4 class="font-semibold text-gray-900 mb-2">Classification Tags ({{ aiInfoTags.length }})</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    v-for="(tag, index) in aiInfoTags"
                    :key="index"
                    class="bg-primary-50 rounded-lg p-3 border border-blue-200"
                  >
                    <div class="flex items-start justify-between mb-1">
                      <h5 class="font-medium text-gray-900">{{ tag.name }}</h5>
                      <span class="text-xs text-gray-500">#{{ index + 1 }}</span>
                    </div>
                    <p class="text-sm text-gray-600">{{ tag.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reasoning -->
            <div v-if="aiReasoning">
              <h4 class="font-semibold text-gray-900 mb-2">Design Reasoning</h4>
              <div class="bg-gray-50 rounded-lg p-3">
                <p class="text-sm text-gray-700">{{ aiReasoning }}</p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
            <div class="flex space-x-2">
              <button
                @click="showFeedbackInput = !showFeedbackInput"
                class="px-4 py-2 text-purple-700 bg-purple-50 border border-purple-300 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <FontAwesomeIcon :icon="['fas', 'comment-dots']" class="mr-2" />
                Improve with Feedback
              </button>
              <button
                @click="regenerateFromScratch"
                class="px-4 py-2 text-purple-700 bg-purple-50 border border-purple-300 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" class="mr-2" />
                Start Over
              </button>
            </div>
            <div class="flex space-x-3">
              <button
                @click="closeGenerator"
                class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                @click="acceptAndCreate"
                :disabled="accepting"
                class="px-4 py-2 text-white bg-green-dark rounded-lg hover:bg-green-700 transition-all shadow-sm disabled:opacity-50"
              >
                <FontAwesomeIcon v-if="accepting" :icon="['fas', 'spinner']" class="animate-spin mr-2" />
                <FontAwesomeIcon v-else :icon="['fas', 'check']" class="mr-2" />
                Accept & Create
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'ClassifierAIGenerator',
  components: {
    FontAwesomeIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close', 'created'],
  data() {
    return {
      // Entity Types
      entityTypes: [],

      // Configuration Options
      configOptions: {
        enableWebSearch: false,
        performanceDataSources: [],
        contextClassifiers: []
      },

      // Available options for selection
      availableDataSources: [
        { key: 'cost_risk', label: 'Cost & Risk', description: 'PMPM costs, total costs, attribution' },
        { key: 'awv', label: 'AWV Rates', description: 'Annual Wellness Visit completion' },
        { key: 'coding', label: 'Coding', description: 'HCC coding and RAF scores' },
        { key: 'attribution', label: 'Attribution', description: 'Patient attribution by payer' },
        { key: 'utilization', label: 'Utilization', description: 'Claims-based utilization metrics' },
        { key: 'vbc', label: 'VBC Programs', description: 'Value-based care participation' },
        { key: 'providers', label: 'Provider Roster', description: 'Provider count and specialty distribution' }
      ],
      availableContextClassifiers: [],

      // Generation Step
      aiDescription: '',
      aiClassifierName: '',
      aiTargetEntityType: '',
      aiGenerating: false,
      aiGenerated: false,
      aiError: null,

      // Generated Results
      aiClassifierConfig: {},
      aiInfoTags: [],
      aiValidation: {},
      aiReasoning: '',

      // Feedback
      showFeedbackInput: false,
      aiFeedback: '',
      originalDescription: '',

      // Creation
      accepting: false,

      // Validation categories for display
      validationCategories: [
        { key: 'prompt_quality', label: 'Prompt Quality' },
        { key: 'infotags', label: 'Tags' },
        { key: 'data_sources', label: 'Data Sources' },
        { key: 'completeness', label: 'Completeness' }
      ]
    };
  },
  computed: {
    canGenerate() {
      return this.aiDescription && this.aiClassifierName && this.aiTargetEntityType;
    }
  },
  methods: {
    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = response.data;
      } catch (error) {
        console.error('Error fetching entity types:', error);
      }
    },

    async fetchContextClassifiers() {
      try {
        const response = await axios.get('/api/infotagtypes/');
        this.availableContextClassifiers = response.data.filter(itt =>
          itt.metadata?.classification_config
        ).map(itt => ({
          id: itt.id,
          name: itt.name,
          target_entity_type: itt.metadata.classification_config.target_entity_type
        }));
      } catch (error) {
        console.error('Error fetching context classifiers:', error);
      }
    },

    async generateWithAI() {
      if (!this.canGenerate) return;

      this.originalDescription = this.aiDescription;
      this.aiGenerating = true;
      this.aiError = null;

      try {
        const response = await axios.post('/api/infotagtypes/generate-classifier/', {
          description: this.aiDescription,
          target_entity_type: this.aiTargetEntityType,
          enable_web_search: this.configOptions.enableWebSearch,
          performance_data_sources: this.configOptions.performanceDataSources,
          context_classifier_ids: this.configOptions.contextClassifiers,
          context: {
            classifier_name: this.aiClassifierName
          }
        });

        if (response.data.success) {
          this.aiClassifierConfig = response.data.classifier_config;
          this.aiInfoTags = response.data.infotags;
          this.aiValidation = response.data.validation;
          this.aiReasoning = response.data.reasoning;
          this.aiGenerated = true;
        } else {
          this.aiError = response.data.error || 'Failed to generate classifier';
        }
      } catch (error) {
        console.error('Error generating classifier:', error);
        this.aiError = error.response?.data?.error || 'An error occurred while generating';
      } finally {
        this.aiGenerating = false;
      }
    },

    async regenerateWithFeedback() {
      if (!this.aiFeedback) return;

      this.aiGenerating = true;
      this.aiError = null;

      try {
        const response = await axios.post('/api/infotagtypes/generate-classifier/', {
          description: this.originalDescription,
          target_entity_type: this.aiTargetEntityType,
          enable_web_search: this.configOptions.enableWebSearch,
          performance_data_sources: this.configOptions.performanceDataSources,
          context_classifier_ids: this.configOptions.contextClassifiers,
          context: {
            classifier_name: this.aiClassifierName
          },
          feedback: this.aiFeedback,
          previous_configuration: {
            classifier_config: this.aiClassifierConfig,
            infotags: this.aiInfoTags
          }
        });

        if (response.data.success) {
          this.aiClassifierConfig = response.data.classifier_config;
          this.aiInfoTags = response.data.infotags;
          this.aiValidation = response.data.validation;
          this.aiReasoning = response.data.reasoning;
          this.showFeedbackInput = false;
          this.aiFeedback = '';
        } else {
          this.aiError = response.data.error || 'Failed to regenerate classifier';
        }
      } catch (error) {
        console.error('Error regenerating classifier:', error);
        this.aiError = error.response?.data?.error || 'An error occurred while regenerating';
      } finally {
        this.aiGenerating = false;
      }
    },

    regenerateFromScratch() {
      this.aiGenerated = false;
      this.aiClassifierConfig = {};
      this.aiInfoTags = [];
      this.aiValidation = {};
      this.aiReasoning = '';
      this.showFeedbackInput = false;
      this.aiFeedback = '';
    },

    async acceptAndCreate() {
      this.accepting = true;

      try {
        // Step 1: Create InfoTagType
        const infoTagTypeData = {
          name: this.aiClassifierConfig.name || this.aiClassifierName,
          UIColor: this.generateRandomColor(),
          metadata: {
            classification_config: this.aiClassifierConfig
          }
        };

        const infoTagTypeResponse = await axios.post('/api/infotagtypes/', infoTagTypeData);
        const newInfoTagType = infoTagTypeResponse.data;

        // Step 2: Create InfoTags
        for (const tag of this.aiInfoTags) {
          await axios.post('/api/infotags/', {
            infoTagType: newInfoTagType.id,
            name: tag.name,
            description: tag.description,
            UIColor: this.generateRandomColor(),
            content_type_model: 'entity'
          });
        }

        this.$emit('created', newInfoTagType);
        this.closeGenerator();
      } catch (error) {
        console.error('Error creating classifier:', error);
        this.aiError = 'Failed to create classifier and tags';
      } finally {
        this.accepting = false;
      }
    },

    closeGenerator() {
      this.resetForm();
      this.$emit('close');
    },

    resetForm() {
      this.aiDescription = '';
      this.aiClassifierName = '';
      this.aiTargetEntityType = '';
      this.configOptions = {
        enableWebSearch: false,
        performanceDataSources: [],
        contextClassifiers: []
      };
      this.aiGenerated = false;
      this.aiClassifierConfig = {};
      this.aiInfoTags = [];
      this.aiValidation = {};
      this.aiReasoning = '';
      this.aiError = null;
      this.showFeedbackInput = false;
      this.aiFeedback = '';
      this.originalDescription = '';
    },

    generateRandomColor() {
      const colors = [
        '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B',
        '#6366F1', '#14B8A6', '#F43F5E', '#06B6D4', '#84CC16'
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    getDataSourceLabels(keys) {
      return keys.map(key => {
        const source = this.availableDataSources.find(s => s.key === key);
        return source ? source.label : key;
      });
    },

    // Display helpers
    getScoreColor(score) {
      if (score >= 85) return 'text-green-600';
      if (score >= 70) return 'text-yellow-600';
      if (score >= 50) return 'text-orange-600';
      return 'text-red-600';
    },

    getRecommendationBadgeClass(recommendation) {
      switch (recommendation) {
        case 'ready_to_use':
          return 'bg-green-100 text-green-800';
        case 'minor_adjustments_recommended':
          return 'bg-yellow-100 text-yellow-800';
        case 'major_revisions_needed':
          return 'bg-orange-100 text-orange-800';
        case 'regenerate_recommended':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    formatRecommendation(recommendation) {
      return recommendation?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
    },

    getIssueIcon(severity) {
      switch (severity) {
        case 'critical': return ['fas', 'circle-exclamation'];
        case 'major': return ['fas', 'triangle-exclamation'];
        case 'minor': return ['fas', 'circle-info'];
        default: return ['fas', 'lightbulb'];
      }
    },

    getIssueColor(severity) {
      switch (severity) {
        case 'critical': return 'text-red-500';
        case 'major': return 'text-orange-500';
        case 'minor': return 'text-yellow-500';
        default: return 'text-blue-500';
      }
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchEntityTypes();
        this.fetchContextClassifiers();
      }
    }
  }
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .transform,
.modal-leave-active .transform {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
}

.modal-enter-from .transform,
.modal-leave-to .transform {
  transform: scale(0.95);
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
